// Table/HeaderCell (20:12) + Table/Cell (20:17), closed by Data/Pagination
// (26:63). Header 30px, body rows 52px, 12px cell padding.
//
// THE ROW CAP IS THE POINT. The seeded volume is 60 documents and 480 terms,
// and no primitive in this library may render an unbounded list — a table
// that quietly paints 480 rows is the legibility failure the PRD's
// cross-cutting property exists to stop. So `maxRows` always applies (25 by
// default) and the count line below the table always states the real total,
// which turns silent truncation into a visible fact.
//
// Numeric columns right-align and switch to .type-numSm; text columns stay
// left in .type-bodyS. The Figma component doc states that rule.

import "./ui.css";
import type { ReactNode } from "react";

export const DEFAULT_MAX_ROWS = 25;

export type TableColumn<T> = {
  key: string;
  header: string;
  cell: (row: T) => ReactNode;
  align?: "left" | "right";
  /** Renders the sorted-column treatment and sets aria-sort. */
  sort?: "ascending" | "descending";
};

export type TableProps<T> = {
  /** Accessible name — the visible title normally lives on the Card header. */
  label: string;
  columns: TableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Rows actually painted. Defaults to DEFAULT_MAX_ROWS (25). */
  maxRows?: number;
  /** The real total behind `rows`, when the caller already paged server-side. */
  total?: number;
  /** Plural noun for the count line, e.g. "documents". */
  unit?: string;
  /** Rendered as the overflow affordance's action when rows are withheld. */
  onShowMore?: () => void;
  showMoreLabel?: string;
  /** Rendered instead of the table when there are no rows at all. */
  empty?: ReactNode;
};

export function Table<T>({
  label,
  columns,
  rows,
  rowKey,
  maxRows = DEFAULT_MAX_ROWS,
  total,
  unit = "rows",
  onShowMore,
  showMoreLabel = "Show more",
  empty,
}: TableProps<T>) {
  if (rows.length === 0 && empty) return <>{empty}</>;

  const visible = rows.slice(0, maxRows);
  const stated = total ?? rows.length;
  const withheld = stated - visible.length;

  return (
    <div>
      <div className="maisight-ui-table-wrap">
        <table className="maisight-ui-table" aria-label={label}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className="type-labelSm"
                  data-align={column.align === "right" ? "right" : undefined}
                  aria-sort={column.sort}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={rowKey(row)}>
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={
                      column.align === "right" ? "type-numSm" : "type-bodyS"
                    }
                    data-align={column.align === "right" ? "right" : undefined}
                  >
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="maisight-ui-table-overflow type-bodyS">
        <span>
          {`Showing ${visible.length} of ${stated} ${unit}`}
        </span>
        {withheld > 0 && onShowMore ? (
          <button
            type="button"
            className="maisight-ui-button type-labelSm"
            data-variant="ghost"
            data-size="sm"
            onClick={onShowMore}
          >
            {showMoreLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}
